import { createSelector } from '@reduxjs/toolkit'
import { createSelectorCache, getOrBuildSelector } from '../../Sinks/Selector/SelectorCache.ts'
import { selectFilteredCube } from '../../Sinks/Selector/SelectFilteredCube.ts'
import { forwardCubeStatus, selectCubeContext } from '../../Sinks/Selector/CubeContext.ts'
import type { RankedBarOpts, TransformResult } from '../Types.ts'
import type { State } from '../../../../../Flux/Slice/index.ts'
import type { SourceId } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import type { TaxaAggregationResult } from '../../Sinks/Selector/TaxaAggregation.ts'
import { aggregateRankedBar } from '../../Sinks/Selector/TaxaAggregationCore.ts'

/**
 * Powers the Ranked Bar sink. Reuses the SAME `selectFilteredCube` (selectedPath prefix + grade
 * already applied) as the other taxa transformers, so zoom is respected; only the final grouping
 * differs. The grouping mode is chosen by {@link aggregateRankedBar}:
 *  - `rankLevel` (a rank NAME) forces a uniform rank,
 *  - else a non-empty `selectedPath` (a `taxonEntry` zoom) ranks the entry's DIRECT children,
 *  - else (root) ranks the deepest-resolved leaf organisms — the legacy behavior.
 */
const selectorCache = createSelectorCache<TransformResult<TaxaAggregationResult>>()

export function selectTaxaRankedBarSingle(
  state: typeof State,
  sourceId: SourceId,
  bucketKey: string | undefined,
  opts: RankedBarOpts,
  optsKey: string
): TransformResult<TaxaAggregationResult> {
  if (!bucketKey) return { status: 'idle' }

  const cacheId = `${sourceId}|${bucketKey}|${optsKey}`

  return getOrBuildSelector(selectorCache, cacheId, () =>
    createSelector(
      [
        (s: typeof State) => selectCubeContext(s, sourceId),
        (s: typeof State) =>
          selectFilteredCube(s, sourceId, { selectedPath: opts.selectedPath, gradeThreshold: opts.gradeThreshold })
      ],
      (ctx, filtered): TransformResult<TaxaAggregationResult> => {
        if (ctx.status !== 'ready') return forwardCubeStatus(ctx)

        const value = aggregateRankedBar({
          filteredBuckets: filtered,
          bucketKey,
          selectedPath: opts.selectedPath,
          rankLevel: opts.rankLevel
        })

        return { status: 'ready', value }
      }
    )
  )(state)
}
